import React, { useState } from 'react';
import { toast } from 'sonner';
import { Download, Eye, ExternalLink, Loader2, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CATEGORY_CONFIG, itemVisual, previewKind, downloadItem, formatSize, formatDate, linkHost } from '@/components/library/libraryMeta';

export const DownloadButton = ({ item, testId, size = 12, className = 'h-7 text-xs' }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadItem(item); } catch (e) { toast.error('Download failed, please retry'); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" onClick={run} disabled={busy} className={`${className} bg-gradient-to-r from-cyan-500 to-blue-600 text-white`} data-testid={testId}>
      {busy ? <Loader2 size={size} className="mr-1 animate-spin" /> : <Download size={size} className="mr-1" />} Download
    </Button>
  );
};

const ItemActions = ({ item, onPreview }) => {
  if (item.kind !== 'file') {
    return (
      <Button asChild size="sm" variant="outline" className="h-7 text-xs border-[hsl(var(--border))]">
        <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`item-open-${item.id}`}><ExternalLink size={12} className="mr-1" /> Open</a>
      </Button>
    );
  }
  return (
    <div className="flex gap-1.5">
      {previewKind(item) && (
        <Button size="sm" variant="outline" onClick={() => onPreview(item)} className="h-7 text-xs border-[hsl(var(--border))]" data-testid={`item-view-${item.id}`}>
          <Eye size={12} className="mr-1" /> View
        </Button>
      )}
      <DownloadButton item={item} testId={`item-download-${item.id}`} />
    </div>
  );
};

export const ItemCard = ({ item, path, onPreview, onEdit, onDelete }) => {
  const v = itemVisual(item);
  const Icon = v.icon;
  const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.general;
  const sub = item.kind === 'file' ? `${(item.ext || '').toUpperCase()} · ${formatSize(item.size)}` : linkHost(item.url);
  return (
    <div className="group flex flex-col rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 transition-[border-color,transform] duration-200 hover:border-cyan-500/30 hover:-translate-y-0.5"
      data-testid={`library-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 shrink-0 rounded-lg border flex flex-col items-center justify-center ${v.cls}`}>
          <Icon size={17} />
          <span className="text-[7px] font-bold tracking-wider mt-0.5">{v.label}</span>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-snug line-clamp-2 text-[hsl(var(--text-primary))]" title={item.title} data-testid={`item-title-${item.id}`}>{item.title}</h3>
          <p className="text-[11px] text-[hsl(var(--text-muted))] truncate mt-0.5" title={item.original_filename || item.url}>{sub}</p>
        </div>
        {item.can_edit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 -mr-1 opacity-60 group-hover:opacity-100" data-testid={`item-menu-${item.id}`}><MoreVertical size={14} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <DropdownMenuItem onClick={() => onEdit(item)} data-testid={`item-edit-${item.id}`}><Pencil size={14} className="mr-2" /> Edit / Move</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(item)} className="text-red-400" data-testid={`item-delete-${item.id}`}><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {item.description && <p className="text-xs text-[hsl(var(--text-muted))] mt-2.5 line-clamp-2">{item.description}</p>}
      {path && <p className="text-[11px] text-cyan-400/80 mt-2 truncate" data-testid={`item-path-${item.id}`}>Library / {path}</p>}
      <div className="flex items-center justify-between gap-2 mt-auto pt-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cat.cls}`}>{cat.label}</span>
        <ItemActions item={item} onPreview={onPreview} />
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-[hsl(var(--border))] text-[10px] text-[hsl(var(--text-muted))]">
        <span className="truncate">{item.user_name}</span>
        <span>{formatDate(item.created_at)}</span>
      </div>
    </div>
  );
};
